import { PinyinSheet } from "./pinyin-worksheet-sheet.js";
import {
  pinyinWorksheetFragment,
  type PinyinWorksheetState,
  readPinyinWorksheetState,
} from "./pinyin-worksheet-state.js";
import { pinyinItems } from "./pinyin-worksheet-words.js";
import {
  concurrency,
  counted,
  pageFetch,
  type SheetKind,
  sheetTitle,
  type SheetToolProperties,
  startSheetTool,
} from "./sheet-tool.js";
import { GlyphLibrary } from "./worksheet-drawing.js";
import { sheetOrder } from "./worksheet-seed.js";

/**
 * The pinyin worksheet generator: the pinyin sheet as a kind of sheet
 * `sheet-tool.ts` draws.
 */

/**
 * The most words one sheet takes, the same as the character sheet's limit on
 * characters. It holds the HSK 1 band whole.
 */
export const wordLimit = 300;

/** The pinyin sheet. */
export const pinyinSheetKind: SheetKind<PinyinWorksheetState> = {
  read: readPinyinWorksheetState,
  fragment: pinyinWorksheetFragment,

  nothingAsked: (state) =>
    !state.tones &&
    !state.pinyin &&
    !state.split &&
    !state.hanzi &&
    !state.lines
      ? "Tick at least one box under Include."
      : undefined,

  title: (state, list) =>
    sheetTitle(state, list, {
      typed: "Pinyin practice",

      saved: "Pinyin practice from my saved words",
    }),

  draw: async ({ state, words, entries, labels }) => {
    const found = await pinyinItems(words, entries, wordLimit, concurrency);
    const left =
      found.missing.length === 0
        ? ""
        : ` Left out because no dictionary entry was found (${found.missing.join(" ")}).`;
    if (found.items.length === 0 && !state.lines) {
      return `None of these words are in the Chinese Boost dictionary.${left}`;
    }

    const library = new GlyphLibrary({ document });
    const pages = new PinyinSheet({ library }).pages(
      state,
      sheetOrder(found.items, state),
      labels,
    );
    const capped = found.capped
      ? ` Only the first ${String(wordLimit)} words are included.`
      : "";

    return {
      nodes: [library.element, ...pages],
      status: `${counted(found.items.length, "word", "words")} on ${counted(pages.length, "page", "pages")}.${capped}${left}`,
    };
  },
};

/** Start the pinyin worksheet generator on its container. */
export function startPinyinWorksheetTool(
  container: HTMLElement,
  properties: SheetToolProperties = pageFetch,
): void {
  startSheetTool(container, pinyinSheetKind, properties);
}
